'use client';

import { useSession } from '@lib/auth/client';
import { useQuery } from '@apollo/client/react';
import { Link } from '@components/link';
import { UserMenu } from '@components/user-menu';
import { routes } from '@routes';
import {
  GQL_TO_STEP_KEY,
  STEP_LABELS,
  STEP_ROUTES,
} from '@utils/onboarding-steps';
import {
  AccountForCurrentUserDocument,
  PmOnboardingSessionStatus,
  PmOnboardingStepKey,
} from '@gql';
import type { OnboardingStepKey } from '@utils/onboarding-steps';

/**
 * Client-side navbar actions that depend on session state.
 * Contains the "List/Manage Properties" link and UserMenu.
 */
export const NavbarActions: React.FC = () => {
  const { data: session, status } = useSession();

  return (
    <div className="flex items-center justify-end">
      <ListPropertiesLink
        isHost={session?.user?.isHost}
        isAuthenticated={status === 'authenticated'}
      />
      <UserMenu />
    </div>
  );
};

/**
 * "List/Manage Properties" link based on host status and onboarding progress
 */
const ListPropertiesLink: React.FC<{
  isHost?: boolean;
  isAuthenticated: boolean;
}> = ({ isHost, isAuthenticated }) => {
  // Query for account data only if user is authenticated and is a host
  const { data, loading } = useQuery(AccountForCurrentUserDocument, {
    skip: !isAuthenticated || !isHost,
    fetchPolicy: 'cache-first',
  });

  // Show skeleton while loading for hosts
  if (isHost && loading) {
    return (
      <span className="ml-4 hidden md:inline-block">
        <span className="h-4 w-36 bg-gray-200 rounded animate-pulse inline-block" />
      </span>
    );
  }

  // Get onboarding session from account (properly typed)
  const account =
    data?.accountForCurrentUser?.__typename === 'Account'
      ? data.accountForCurrentUser
      : null;
  const pmOnboardingSession = account?.pmOnboardingSession;
  const onboardingCompletedAt = account?.onboardingCompletedAt ?? null;

  // Determine link text and destination. Use onboardingCompletedAt (server
  // source of truth) for linking to /a/listings to avoid redirect loop when
  // PmOnboardingSession.status is Completed but account.onboardingCompletedAt
  // is still null.
  let to: string;
  let label: string;

  if (!isHost) {
    to = routes.list;
    label = 'List your Properties';
  } else if (onboardingCompletedAt) {
    // Server says onboarding complete - safe to link to /a/listings
    to = routes.accountListings;
    label = 'Manage your Listings';
  } else if (
    pmOnboardingSession?.status === PmOnboardingSessionStatus.Ineligible
  ) {
    return null;
  } else {
    // In onboarding: use session current step first, fallback to import (step-2)
    const apiStepKey = pmOnboardingSession?.currentStepKey;
    const stepKey: OnboardingStepKey =
      apiStepKey && apiStepKey in GQL_TO_STEP_KEY
        ? GQL_TO_STEP_KEY[apiStepKey as PmOnboardingStepKey]
        : 'import';
    to = STEP_ROUTES[stepKey] ?? routes.accountListings;
    label = `Continue: ${STEP_LABELS[stepKey] ?? 'onboarding'}`;
  }

  return (
    <Link
      to={to}
      className="ml-4 hidden text-sm md:inline-block text-primary-main"
    >
      {label}
    </Link>
  );
};
